import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  keyframes,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
// Animation keyframes
const bounceIn = keyframes`
    0% { transform: scale(0); opacity: 0; }
    50% { transform: scale(1.2); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  `;

const shake = keyframes`
    0% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    50% { transform: translateX(5px); }
    75% { transform: translateX(-5px); }
    100% { transform: translateX(0); }
  `;

const fadeIn = keyframes`
    0% { opacity: 0; transform: translateY(-10px); }
    100% { opacity: 1; transform: translateY(0); }
  `;

export const CommonDialog = ({ dialog, onClose }) => {
  // Get color based on type
  const getColor = () => {
    switch (dialog.type) {
      case "success":
        return "success.main";
      case "error":
      case "delete":
        return "error.main";
      case "fail":
        return "error.main";
      case "simple":
        if (dialog.subtype === "delete") return "warning.main";
        return "primary.main";
      default:
        return "primary.main";
    }
  };
  // Get icon based on type
  const getIcon = () => {
    const baseStyle = {
      fontSize: 64,
      color: getColor(),
    };

    switch (dialog.type) {
      case "success":
        return (
          <CheckCircleOutlineIcon
            sx={{
              ...baseStyle,
              animation: `${bounceIn} 0.6s ease-out`,
            }}
          />
        );
      case "delete":
        return (
          <DeleteIcon
            sx={{
              ...baseStyle,
              animation: `${bounceIn} 0.6s ease-out`,
            }}
          />
        );
      case "fail":
        return (
          <ErrorOutlineIcon
            sx={{
              ...baseStyle,
              animation: `${shake} 0.5s ease-in-out`,
            }}
          />
        );
      case "simple":
        if (dialog.subtype === "delete") {
          return <WarningAmberIcon sx={baseStyle} />;
        }
        return <InfoOutlinedIcon sx={baseStyle} />;
      default:
        return <InfoOutlinedIcon sx={baseStyle} />;
    }
  };

  // Handle dialog close
  const handleClose = (event) => {
    if (dialog.onConfirm) {
      dialog.onConfirm();
    }
    onClose();
  };

  const isSpecialDialog = dialog.type === "success" || dialog.type === "fail";

  return (
    <Dialog
      open={dialog.isOpen}
      onClose={handleClose}
      disableEscapeKeyDown={dialog.disableEscapeKeyDown}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minWidth: "350px",
          animation: `${fadeIn} 0.3s ease-out`,
          ...(isSpecialDialog && {
            textAlign: "center",
            padding: 4,
          }),
        },
      }}
    >
      {/* Special Dialogs (Success/Fail) */}
      {isSpecialDialog ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
              "& svg": {
                focusable: "false",
              },
            }}
          >
            {getIcon()}
          </Box>

          <DialogTitle
            id="dialog-title"
            sx={{
              color: getColor(),
              fontSize: "1.5rem",
              fontWeight: "bold",
            }}
          >
            {dialog.title}
          </DialogTitle>

          <DialogContent
            id="dialog-description"
            sx={{ typography: "body1", px: 3 }}
          >
            {dialog.message}
          </DialogContent>

          <DialogActions
            sx={{
              justifyContent: "center",
              pt: 2,
              pb: 1,
            }}
          >
            <Button
              onClick={handleClose}
              variant="contained"
              autoFocus
              sx={{
                backgroundColor: getColor(),
                color: "white",
                px: 4,
                py: 1,
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: getColor(),
                  opacity: 0.9,
                },
              }}
            >
              {dialog.buttonText || "OK"}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle id="dialog-title" sx={{ color: getColor() }}>
            {dialog.title}
          </DialogTitle>
          <DialogContent id="dialog-description">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: 1,
                "& svg": {
                  focusable: "false",
                },
              }}
            >
              {getIcon()}
              <Box sx={{ typography: "body1" }}>{dialog.message}</Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ pt: 1 }}>
            <Button autoFocus onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleClose}
              variant={dialog.subtype === "delete" ? "contained" : "text"}
              autoFocus
              aria-label={dialog.buttonLabel || "Confirm dialog action"}
              sx={{
                color: dialog.subtype === "delete" ? "white" : getColor(),
                backgroundColor:
                  dialog.subtype === "delete" ? getColor() : "transparent",

                borderRadius: 2,
                "&:hover": {
                  backgroundColor:
                    dialog.subtype === "delete"
                      ? getColor()
                      : `${getColor()}10`,
                  opacity: 0.9,
                },
              }}
            >
              {dialog.buttonText || "OK"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
